import _typeof from "@babel/runtime-corejs3/helpers/esm/typeof";
import _asyncToGenerator from "@babel/runtime-corejs3/helpers/esm/asyncToGenerator";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _Array$from from "@babel/runtime-corejs3/core-js-stable/array/from";
import _startsWithInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/starts-with";
import _regeneratorRuntime from "@babel/runtime-corejs3/regenerator";
// RelayOS KiwiIRC - Widget Entry Point

import Vue from 'vue';
import _ from 'lodash';
import JSON5 from 'json5';
import Logger from '@/libs/Logger';
import ConfigLoader from '@/libs/ConfigLoader';
import getState from '@/libs/state';
import ThemeManager from '@/libs/ThemeManager';
import GlobalApi from '@/libs/GlobalApi';
import * as Misc from '@/helpers/Misc';
import { loadHostConfig } from '@/utils/host-config-loader';
import { loadRenderingModeCSS } from '@/utils/rendering-mode-loader';

// Import widget components
import WidgetContainer from '@/components/widget/WidgetContainer';
import MessageList from '@/components/MessageList';
import ControlInput from '@/components/ControlInput';

// Import global styles
import 'font-awesome/css/font-awesome.css';

// Set up logging
var logLevelMatch = window.location.href.match(/kiwi-loglevel=(\d)/);
if (logLevelMatch && logLevelMatch[1]) {
  var newLevel = parseInt(logLevelMatch[1], 10);
  Logger.setLevel(newLevel);
  Logger('Logging level set to', newLevel);
}
var log = Logger.namespace('widget');

// Create the global API
var api = window.kiwi = GlobalApi.singleton();

// Set the rendering mode in the global API
api.setRenderingMode('widget');

/**
 * RelayOS KiwiIRC Widget
 *
 * This class provides the API for embedding the RelayOS KiwiIRC widget in any website.
 */
var RelayOSWidget = /*#__PURE__*/function () {
  /**
   * Create a new RelayOS KiwiIRC widget
   * @param {Object} options - Widget configuration options
   */
  function RelayOSWidget() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, RelayOSWidget);
    this.options = {
      title: options.title || 'Chat with us',
      position: options.position || 'bottom-right',
      initialState: options.initialState || 'minimized',
      server: options.server || null,
      channel: options.channel || null,
      theme: options.theme || null,
      container: options.container || null,
      onStateChange: options.onStateChange || null,
      onConnect: options.onConnect || null,
      onDisconnect: options.onDisconnect || null,
      onMessage: options.onMessage || null
    };
    this.app = null;
    this.state = getState();
    this.initialized = false;

    // Initialize the widget
    this.init();
  }

  /**
   * Initialize the widget
   * @private
   */
  return _createClass(RelayOSWidget, [{
    key: "init",
    value: (function () {
      var _init = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _t;
        return _regeneratorRuntime.wrap(function (_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _context.prev = 0;
              _context.next = 1;
              return this.loadConfig();
            case 1:
              // Create the widget container
              this.createContainer();

              // Initialize the theme
              this.initTheme();

              // Mount the Vue app
              this.mountApp();

              // Connect to the IRC server
              if (this.options.server) {
                this.connect();
              }
              this.initialized = true;
              log.info('Widget initialized');
              _context.next = 3;
              break;
            case 2:
              _context.prev = 2;
              _t = _context["catch"](0);
              log.error('Error initializing widget:', _t);
            case 3:
            case "end":
              return _context.stop();
          }
        }, _callee, this, [[0, 2]]);
      }));
      function init() {
        return _init.apply(this, arguments);
      }
      return init;
    }()
    /**
     * Load the widget configuration
     * @private
     */
    )
  }, {
    key: "loadConfig",
    value: (function () {
      var _loadConfig = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        var configFile, configObj, configLoader, config, _t2;
        return _regeneratorRuntime.wrap(function (_context2) {
          while (1) switch (_context2.prev = _context2.next) {
            case 0:
              configFile = 'static/widget-config.json';
              configObj = null; // Check for a meta tag with configuration
              if (document.querySelector('meta[name="kiwiconfig"]')) {
                configFile = document.querySelector('meta[name="kiwiconfig"]').content;
              }

              // Check for a script tag with configuration
              if (document.querySelector('script[name="kiwiconfig"]')) {
                try {
                  configObj = JSON5.parse(document.querySelector('script[name="kiwiconfig"]').innerHTML);
                } catch (error) {
                  log.error('Error parsing config from script tag:', error);
                }
              }

              // Create a config loader
              configLoader = new ConfigLoader();
              configLoader.addValueReplacement('protocol', window.location.protocol).addValueReplacement('wsprotocol', window.location.protocol === 'https:' ? 'wss:' : 'ws:').addValueReplacement('tls', window.location.protocol === 'https:').addValueReplacement('hostname', window.location.hostname).addValueReplacement('host', window.location.host).addValueReplacement('port', window.location.port || (window.location.protocol === 'https:' ? 443 : 80)).addValueReplacement('hash', (window.location.hash || '').substr(1)).addValueReplacement('query', (window.location.search || '').substr(1)).addValueReplacement('referrer', window.document.referrer);

              // Load the configuration
              if (!configObj) {
                _context2.next = 2;
                break;
              }
              _context2.next = 1;
              return configLoader.loadFromObj(configObj);
            case 1:
              _t2 = _context2.sent;
              _context2.next = 4;
              break;
            case 2:
              _context2.next = 3;
              return configLoader.loadFromUrl(configFile);
            case 3:
              _t2 = _context2.sent;
            case 4:
              config = _t2;
              _context2.next = 5;
              return loadHostConfig(config);
            case 5:
              config = _context2.sent;
              // Apply the configuration to the state
              Misc.dedotObject(config);
              this.applyConfig(config);

              // Override with options passed to the constructor
              if (this.options.server) {
                this.state.settings.startupOptions.server = this.options.server;
              }
              if (this.options.channel) {
                this.state.settings.startupOptions.channel = this.options.channel;
              }
              if (this.options.theme) {
                this.state.settings.theme = this.options.theme;
              }
            case 6:
            case "end":
              return _context2.stop();
          }
        }, _callee2, this);
      }));
      function loadConfig() {
        return _loadConfig.apply(this, arguments);
      }
      return loadConfig;
    }()
    /**
     * Apply the configuration to the state
     * @param {Object} config - The configuration object
     * @private
     */
    )
  }, {
    key: "applyConfig",
    value: function applyConfig(config) {
      // Recursively merge an object onto another via Vue.$set
      function applyConfigObj(obj, target) {
        _.each(obj, function (val, key) {
          if (_typeof(val) === 'object') {
            if (_typeof(target[key]) !== 'object') {
              Vue.set(target, key, _.isArray(val) ? [] : {});
            }
            applyConfigObj(val, target[key]);
          } else {
            Vue.set(target, key, val);
          }
        });
      }
      applyConfigObj(config, this.state.settings);
    }

    /**
     * Create the widget container
     * @private
     */
  }, {
    key: "createContainer",
    value: function createContainer() {
      // If a container is specified, use it
      if (this.options.container) {
        if (typeof this.options.container === 'string') {
          this.container = document.querySelector(this.options.container);
        } else {
          this.container = this.options.container;
        }
        if (!this.container) {
          throw new Error("Container not found: ".concat(this.options.container));
        }
      } else {
        // Create a new container
        this.container = document.createElement('div');
        this.container.id = 'relayos-widget-container';
        document.body.appendChild(this.container);
      }
    }

    /**
     * Initialize the theme
     * @private
     */
  }, {
    key: "initTheme",
    value: (function () {
      var _initTheme = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee3() {
        var themeMgr, _t3;
        return _regeneratorRuntime.wrap(function (_context3) {
          while (1) switch (_context3.prev = _context3.next) {
            case 0:
              themeMgr = ThemeManager.instance(this.state);
              api.setThemeManager(themeMgr);

              // Load the rendering mode-specific CSS
              _context3.prev = 1;
              _context3.next = 2;
              return loadRenderingModeCSS();
            case 2:
              _context3.next = 4;
              break;
            case 3:
              _context3.prev = 3;
              _t3 = _context3["catch"](1);
              log.error('Failed to load rendering mode CSS:', _t3);
            case 4:
            case "end":
              return _context3.stop();
          }
        }, _callee3, this, [[1, 3]]);
      }));
      function initTheme() {
        return _initTheme.apply(this, arguments);
      }
      return initTheme;
    }()
    /**
     * Mount the Vue app
     * @private
     */
    )
  }, {
    key: "mountApp",
    value: function mountApp() {
      var self = this;

      // Create the Vue app
      this.app = new Vue({
        el: this.container,
        data: function data() {
          return {
            title: self.options.title,
            position: self.options.position,
            initialState: self.options.initialState,
            minimized: self.options.initialState === 'minimized'
          };
        },
        computed: {
          $state: function $state() {
            return getState();
          }
        },
        methods: {
          onStateChange: function onStateChange(state) {
            if (self.options.onStateChange) {
              self.options.onStateChange(state);
            }
          }
        },
        render: function render(h) {
          return h(WidgetContainer, {
            props: {
              title: this.title,
              position: this.position,
              initialState: this.initialState
            },
            on: {
              'state-change': this.onStateChange
            }
          }, [h('div', {
            class: 'kiwi-widget-content'
          }, [h(MessageList), h(ControlInput)])]);
        }
      });

      // Make the app instance available via the API
      api.setVueInstance(this.app);
    }

    /**
     * Connect to the IRC server
     * @public
     */
  }, {
    key: "connect",
    value: function connect() {
      if (!this.initialized) {
        log.warn('Widget not initialized yet, connection deferred');
        return;
      }

      // TODO: Implement connection logic
      log.info('Connecting to IRC server');
      if (this.options.onConnect) {
        this.options.onConnect();
      }
    }

    /**
     * Disconnect from the IRC server
     * @public
     */
  }, {
    key: "disconnect",
    value: function disconnect() {
      if (!this.initialized) {
        log.warn('Widget not initialized yet');
        return;
      }

      // TODO: Implement disconnection logic
      log.info('Disconnecting from IRC server');
      if (this.options.onDisconnect) {
        this.options.onDisconnect();
      }
    }

    /**
     * Minimize the widget
     * @public
     */
  }, {
    key: "minimize",
    value: function minimize() {
      if (!this.initialized || !this.app) {
        log.warn('Widget not initialized yet');
        return;
      }
      var widgetContainer = this.app.$children[0];
      if (widgetContainer && typeof widgetContainer.minimize === 'function') {
        widgetContainer.minimize();
      }
    }

    /**
     * Expand the widget
     * @public
     */
  }, {
    key: "expand",
    value: function expand() {
      if (!this.initialized || !this.app) {
        log.warn('Widget not initialized yet');
        return;
      }
      var widgetContainer = this.app.$children[0];
      if (widgetContainer && typeof widgetContainer.expand === 'function') {
        widgetContainer.expand();
      }
    }

    /**
     * Send a message to the current channel
     * @param {string} message - The message to send
     * @public
     */
  }, {
    key: "sendMessage",
    value: function sendMessage(message) {
      if (!this.initialized) {
        log.warn('Widget not initialized yet');
        return;
      }

      // TODO: Implement message sending logic
      log.info('Sending message:', message);
    }

    /**
     * Join a channel
     * @param {string} channel - The channel to join
     * @public
     */
  }, {
    key: "joinChannel",
    value: function joinChannel(channel) {
      if (!this.initialized) {
        log.warn('Widget not initialized yet');
        return;
      }

      // TODO: Implement channel joining logic
      log.info('Joining channel:', channel);
    }

    /**
     * Leave a channel
     * @param {string} channel - The channel to leave
     * @public
     */
  }, {
    key: "leaveChannel",
    value: function leaveChannel(channel) {
      if (!this.initialized) {
        log.warn('Widget not initialized yet');
        return;
      }

      // TODO: Implement channel leaving logic
      log.info('Leaving channel:', channel);
    }

    /**
     * Set the theme
     * @param {string} theme - The theme name
     * @public
     */
  }, {
    key: "setTheme",
    value: function setTheme(theme) {
      if (!this.initialized) {
        log.warn('Widget not initialized yet');
        return;
      }
      api.getThemeManager().setTheme(theme);
    }

    /**
     * Get the current theme
     * @returns {string} - The current theme name
     * @public
     */
  }, {
    key: "getTheme",
    value: function getTheme() {
      if (!this.initialized) {
        log.warn('Widget not initialized yet');
        return null;
      }
      return api.getThemeManager().getCurrentTheme();
    }

    /**
     * Get the available themes
     * @returns {Array} - The available themes
     * @public
     */
  }, {
    key: "getAvailableThemes",
    value: function getAvailableThemes() {
      if (!this.initialized) {
        log.warn('Widget not initialized yet');
        return [];
      }
      return api.getThemeManager().getAvailableThemes();
    }
  }]);
}(); // Create a global RelayOSWidget constructor
window.RelayOSWidget = RelayOSWidget;

// Auto-initialize if the data-auto-init attribute is present
document.addEventListener('DOMContentLoaded', function () {
  var scriptTag = document.querySelector('script[data-relayos-widget]');
  if (scriptTag && scriptTag.getAttribute('data-auto-init') !== 'false') {
    // Parse options from data attributes
    var options = {};
    _Array$from(scriptTag.attributes).forEach(function (attr) {
      var _context4;
      if (_startsWithInstanceProperty(_context4 = attr.name).call(_context4, 'data-')) {
        var optionName = attr.name.replace('data-', '').replace(/-([a-z])/g, function (g) {
          return g[1].toUpperCase();
        });
        if (optionName !== 'relayosWidget' && optionName !== 'autoInit') {
          options[optionName] = attr.value;
        }
      }
    });

    // Create the widget
    return new RelayOSWidget(options);
  }
  return null;
});
export default RelayOSWidget;